import styled, { keyframes } from '@/styles';

import { ROW_END_ATTR } from './fit';

// GRAVY-10878 "Ops on duty today". Every value is from the UI spec (§3, §4,
// §15), measured off the approved prototype. Kept here rather than in the
// .dashv2 CSS because the bar also renders on the classic dashboard, where
// those variables do not exist.
export const OPS_COLORS = {
  green: 'rgb(68,167,53)',
  darkGreen: 'rgb(46,135,34)',
  red: 'rgb(207,4,12)',
  ink: 'rgb(28,28,28)',
  muted: 'rgb(91,91,91)',
  grey: 'rgb(151,151,151)',
  disabled: 'rgb(217,217,217)',
  border: 'rgb(218,218,218)',
  cardBg: 'rgb(252,252,252)',
} as const;

const C = OPS_COLORS;

export const Bar = styled('section', {
  position: 'relative',
  // Above the title row and tabs, so an open popover paints over them.
  zIndex: 5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '12px 24px',
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '62px',
  margin: '24px 0 0',
  padding: '12px 16px',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: `inset 0 0 0 1px ${C.border}`,
  color: C.ink,
  fontFamily:
    'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontSize: '16px',
  lineHeight: 1.4,
  textAlign: 'left',
  '& *': { boxSizing: 'border-box' },
  '@phoneOnly': { margin: '16px 0 0' },
  variants: {
    // The page has no side padding on phones (the classic dashboard),
    // so the bar keeps its own 16px from the screen edge.
    flush: {
      true: { '@phoneOnly': { margin: '16px 16px 0', width: 'auto' } },
    },
  },
});

// Two faint green ellipses whose lower arcs glow along the top edge.
export const Glow = styled('div', {
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  borderRadius: '12px',
  pointerEvents: 'none',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    background: C.green,
  },
  '&::before': {
    left: '144px',
    top: '-142px',
    width: '663px',
    height: '159px',
    opacity: 0.08,
  },
  '&::after': {
    left: '273px',
    top: '-146px',
    width: '842px',
    height: '162px',
    opacity: 0.12,
  },
});

export const Label = styled('h2', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
  margin: 0,
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  color: C.ink,
  '& svg': { flexShrink: 0, width: '20px', height: '20px', color: C.ink },
});

// Natural width (as in the prototype): beside the label while every
// entry fits on that row, right-aligned by the bar's space-between. When
// they do not, the whole list wraps under the label as a unit, left-aligned
// at natural entry widths, and wraps inside itself only once it is wider
// than the bar. useFitCount therefore measures the bar, not the list.
export const List = styled('ul', {
  position: 'relative',
  flex: '0 1 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  gap: '8px 16px',
  minWidth: 0,
  margin: 0,
  padding: 0,
  listStyle: 'none',
  // DB-03: a divider after each entry but the last, as in the
  // prototype. Set here, not on Item, because the "+N more" list renders the
  // same Item in a column, where a side border would be wrong. The 1px sits
  // inside Item's border-box width, so useFitCount's measurements include it.
  '& > li:not(:last-child)': { borderRight: `1px solid ${C.border}` },
  // The last entry of a wrapped row has nothing after it on that row, so no
  // divider shows. Made transparent rather than removed: dropping the 1px
  // would change the entry's width and could move where the list wraps.
  // `data-row-end` is set from the layout (markRowEnds).
  [`& > li[${ROW_END_ATTR}]`]: { borderRightColor: 'transparent' },
});

export const Item = styled('li', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  minWidth: '220px',
  // Never wider than the bar; a long name shortens with an ellipsis.
  maxWidth: '100%',
  height: '24px',
  margin: 0,
  padding: '0 12px 0 0',
});

export const Who = styled('span', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

export const Dot = styled('span', {
  flexShrink: 0,
  borderRadius: '50%',
  variants: {
    on: { true: { background: C.green }, false: { background: C.grey } },
    size: {
      sm: { width: '8px', height: '8px' },
      md: { width: '10px', height: '10px' },
    },
  },
  defaultVariants: { on: true, size: 'sm' },
});

export const Name = styled('span', {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '16px',
  fontWeight: 600,
});

export const Time = styled('span', {
  fontSize: '16px',
  fontWeight: 400,
  color: C.ink,
  variants: {
    off: { true: { fontWeight: 700, color: C.red } },
  },
});

export const CallButton = styled('button', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '24px',
  height: '24px',
  padding: 0,
  border: 'none',
  borderRadius: '100px',
  background: C.green,
  color: '#fff',
  cursor: 'pointer',
  '& svg': { width: '14px', height: '14px' },
  '&:hover': { filter: 'brightness(0.92)' },
  '&:focus-visible': {
    outline: `2px solid ${C.darkGreen}`,
    outlineOffset: '2px',
  },
  // Off duty is a grey, disabled button that opens nothing.
  '&[aria-disabled="true"]': {
    background: C.disabled,
    cursor: 'not-allowed',
    filter: 'none',
  },
});

export const MoreButton = styled('button', {
  height: '24px',
  padding: '0 4px',
  border: 'none',
  background: 'none',
  color: C.darkGreen,
  fontFamily: 'inherit',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:hover': { color: C.green, textDecoration: 'underline' },
  '&:focus-visible': {
    outline: `2px solid ${C.darkGreen}`,
    outlineOffset: '2px',
    borderRadius: '4px',
  },
});

// Not positioned: the full-list popover hangs off the right edge of the
// list (which spans the bar once it wraps), not off this control, so it
// never runs past the screen edge wherever the control lands.
export const MoreItem = styled('li', {
  position: 'static',
  display: 'flex',
  alignItems: 'center',
  height: '24px',
  margin: 0,
});

// No-one-on-duty message: a bold lead, then the rest, flowing as one
// paragraph. With the label kept beside it, the prototype's two flex
// columns squeeze the rest into a narrow column at 1024, so it wraps as
// plain text instead. `flex: 1` keeps it on the label's line until 240px.
const popIn = keyframes({
  from: { opacity: 0, transform: 'translateY(-4px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});

// The popover sits 12px under the entry and 8px in from its left edge.
export const POPOVER_CSS = {
  top: 'calc(100% + 12px)',
  left: '8px',
  zIndex: 20,
  animation: `${popIn} .18s ease`,
};

// A contact card opened inside the "+N more" list sits further down and
// in, over the list's own card, so the two edges never line up.
export const STACKED_POPOVER_CSS = {
  ...POPOVER_CSS,
  top: 'calc(100% + 16px)',
  left: '24px',
  zIndex: 30,
};

// A card that would run past the screen's right edge hangs from the
// entry's right side instead.
export const RIGHT_ALIGNED_CSS = { left: 'auto', right: '8px' };

export const MORE_POPOVER_CSS = {
  top: 'calc(100% + 12px)',
  right: 0,
  left: 'auto',
  zIndex: 20,
  animation: `${popIn} .18s ease`,
};

export const Card = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: '201px',
  width: 'max-content',
  // Never wider than the screen, less its 16px edges.
  maxWidth: 'calc(100vw - 32px)',
  padding: '16px',
  borderRadius: '12px',
  background: C.cardBg,
  boxShadow: `0 0 0 1px ${C.border}, 0 2px 12px 0 rgba(0,0,0,0.12)`,
  color: C.ink,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: 1.4,
  textAlign: 'left',
  cursor: 'auto',
  variants: {
    layer: {
      stacked: {
        boxShadow: `0 0 0 1px ${C.border}, 0 12px 32px 0 rgba(0,0,0,0.24)`,
      },
    },
  },
});

export const MoreCard = styled(Card, {
  gap: '8px',
  '& ul': {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
});

export const ContactName = styled('p', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  margin: 0,
  fontSize: '18px',
  fontWeight: 700,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
});

export const ContactShift = styled('p', { margin: 0, whiteSpace: 'nowrap' });

export const ContactPhoneRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
});

export const ContactNumber = styled('span', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
  '& svg': { flexShrink: 0, width: '14px', height: '14px', color: C.ink },
  '& a': { color: 'inherit' },
});

export const CopyButton = styled('button', {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '20px',
  height: '20px',
  padding: 0,
  border: 'none',
  background: 'none',
  color: C.green,
  // The "Copied" tip inside takes the bar's Manrope, not the button's Arial.
  font: 'inherit',
  cursor: 'pointer',
  '&:focus-visible': {
    outline: `2px solid ${C.darkGreen}`,
    outlineOffset: '2px',
    borderRadius: '4px',
  },
});

export const CopiedTip = styled('span', {
  position: 'absolute',
  bottom: '28px',
  left: '50%',
  padding: '4px 8px',
  borderRadius: '12px',
  background: C.ink,
  color: '#fff',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  opacity: 0,
  transform: 'translate(-50%, 4px)',
  transition: 'opacity .15s ease, transform .15s ease',
  variants: {
    shown: { true: { opacity: 1, transform: 'translate(-50%, 0)' } },
  },
});

export const NoNumber = styled('p', { margin: 0, color: C.muted });

// Screen-reader-only live text ("Copied").
export const SrOnly = styled('span', {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
});
